import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import i18n, { LanguageCode, getLanguageConfig, SUPPORTED_LANGUAGES } from '../i18n';

interface LanguageState {
  // Current language
  language: LanguageCode;
  isRTL: boolean;
  
  // Actions
  setLanguage: (lang: LanguageCode) => void;
  
  // Helpers
  getAvailableLanguages: () => typeof SUPPORTED_LANGUAGES;
  getCurrentLanguageConfig: () => typeof SUPPORTED_LANGUAGES[number] | undefined;
}

export const useLanguageStore = create<LanguageState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state (default: English)
        language: (i18n.language as LanguageCode) || 'en',
        isRTL: (i18n.language as LanguageCode) === 'fa' || (i18n.language as LanguageCode) === 'ar',
        
        // Set language
        setLanguage: (lang) => {
          const config = getLanguageConfig(lang);
          
          if (!config) {
            console.error(`Language ${lang} not supported`);
            return;
          }
          
          // Update i18n (این خودش document را update می‌کند through event listener)
          i18n.changeLanguage(lang);
          
          // Update store
          set({
            language: lang,
            isRTL: config.dir === 'rtl',
          });
        },
        
        // Get available languages
        getAvailableLanguages: () => SUPPORTED_LANGUAGES,
        
        // Get current language config
        getCurrentLanguageConfig: () => {
          return getLanguageConfig(get().language);
        },
      }),
      {
        name: 'language-store',
        // فقط language را persist کنیم
        partialize: (state) => ({ language: state.language }),
        
        // بعد از load، language را اعمال کنیم
        onRehydrateStorage: () => (state) => {
          if (state?.language) {
            i18n.changeLanguage(state.language);
          }
        },
      }
    ),
    { name: 'LanguageStore' }
  )
);

// Custom hook برای راحتی استفاده
export const useLanguage = () => {
  const language = useLanguageStore((state) => state.language);
  const isRTL = useLanguageStore((state) => state.isRTL);
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  const getAvailableLanguages = useLanguageStore((state) => state.getAvailableLanguages);
  const getCurrentLanguageConfig = useLanguageStore((state) => state.getCurrentLanguageConfig);
  
  return {
    language,
    isRTL,
    setLanguage,
    availableLanguages: getAvailableLanguages(),
    currentLanguage: getCurrentLanguageConfig(),
  };
};
